import { assessmentTranslator } from './assessmentLanguage';
import { curriculumSourceParagraphs } from './assessmentDocument';
import { AlignmentType, Document, HeadingLevel, SectionType } from 'docx';
import { paragraph, heading, markedParagraph, answerLines, rule, assessmentStyles, assessmentPage, assessmentFooter, questionVisuals } from './assessmentDocument';

// `visuals` is the WeakMap from prepareQuestionVisuals; without it, tables still export but figures do not.
export function buildWorksheetDocument(worksheet, form = {}, visuals = new WeakMap()) {
    const translate = assessmentTranslator(worksheet.outputLanguage || form.outputLanguage || 'English');
    const subject = worksheet.subject || form.subject || translate('Assessment');
    const grade = worksheet.grade || form.grade || '';
    const title = worksheet.title || `${subject} ${translate('Worksheet')}`;
    const totalMarks = worksheet.totalMarks ?? form.totalMarks ?? 0;
    const difficulty = worksheet.difficulty || form.difficulty || 'Core';
    const workingSpace = (item) => answerLines(item.answerSpaceLines ?? Math.min(8, Math.max(2, Number(item.marks) || 2)));
    const questionContent = (worksheet.questions || []).flatMap((question, index) => {
        const subQuestions = question.subQuestions || [];
        const lines = subQuestions.length ? [] : workingSpace(question);
        return [
            markedParagraph(question.questionNumber || `${index + 1}`, question.questionText, question.marks, { keepNext: true }),
            ...questionVisuals(question, visuals.get(question)),
            ...lines,
            ...subQuestions.flatMap((sub, subIndex) => {
                const subLines = workingSpace(sub);
                return [markedParagraph(sub.label || `${index + 1}.${subIndex + 1}`, sub.text, sub.marks, { keepNext: subLines.length > 0 }), ...subLines];
            })
        ];
    });
    const memoContent = (worksheet.memorandum || []).flatMap((memo, index) => [
        paragraph(memo.questionNumber || `${translate('Question')} ${index + 1}`, { heading: HeadingLevel.HEADING_2 }),
        ...(memo.solutionSteps || []).map((step) => paragraph(step, { bullet: { level: 0 }, spacing: { after: 160 } })),
        paragraph(`${translate('Final answer')}: ${memo.finalAnswer || ''}`, { spacing: { after: 240 } })
    ]);

    return new Document({
        title,
        creator: 'Marksure',
        styles: assessmentStyles,
        sections: [
            {
                properties: { page: assessmentPage },
                children: [
                    paragraph(translate('MARKSURE WORKSHEET'), { alignment: AlignmentType.CENTER, spacing: { before: 500, after: 360 } }),
                    paragraph(title, { heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER }),
                    paragraph(`${translate('GRADE')} ${grade} | ${subject.toUpperCase()}`, { alignment: AlignmentType.CENTER }),
                    paragraph(`${translate('TERM')} ${worksheet.term || form.term || 1}`, { alignment: AlignmentType.CENTER, spacing: { after: 600 } }),
                    paragraph(`${translate('MARKS')}: ${totalMarks}`),
                    paragraph(`${translate('DIFFICULTY')}: ${translate(difficulty)}`, { spacing: { after: 300 } }),
                    ...[translate('SCHOOL NAME:'), translate('CLASS:'), translate('SURNAME:'), translate('NAME:'), translate('DATE:')].map((label) => paragraph(label, { border: { bottom: rule }, spacing: { before: 300, after: 240, line: 400 } })),
                    paragraph(`${translate('MARKS OBTAINED')}: __________________ / ${totalMarks}`, { spacing: { before: 400 } })
                ]
            },
            {
                properties: { type: SectionType.NEXT_PAGE, page: { ...assessmentPage, pageNumbers: { start: 1 } } },
                footers: { default: assessmentFooter(grade, subject, translate('Learner worksheet'), translate('Grade')) },
                children: [
                    heading(translate('LEARNER WORKSHEET')),
                    paragraph(title),
                    ...questionContent,
                    paragraph(`${translate('TOTAL')}: ${totalMarks} ${translate('MARKS')}`, { alignment: AlignmentType.RIGHT, spacing: { before: 360 } }),
                    paragraph(translate('END OF LEARNER WORKSHEET'), { alignment: AlignmentType.CENTER, spacing: { before: 240 } })
                ]
            },
            {
                properties: { type: SectionType.NEXT_PAGE, page: { ...assessmentPage, pageNumbers: { start: 1 } } },
                footers: { default: assessmentFooter(grade, subject, translate('Memorandum'), translate('Grade')) },
                children: [
                    heading(translate('MEMORANDUM AND MARKING GUIDELINES')),
                    paragraph(`${title} | ${translate('Grade')} ${grade} | ${totalMarks} ${translate('marks')}`),
                    ...memoContent,
                    ...curriculumSourceParagraphs(worksheet.curriculumSources, translate, worksheet.curriculumNotice)
                ]
            }
        ]
    });
}
